import { useReactiveVar } from '@apollo/client/react';
import AppStore from 'assets/images/AppStore.png';
import GooglePlay from 'assets/images/GooglePlay.png';
import Title from 'components/Title/Title';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import QRCode from 'react-qr-code';
import { useSearchParams } from 'react-router';
import { uiVar } from 'state/vars';
import styled from 'styled-components';
import BackgroundImage from 'ui/atoms/BackgroundImage/BackgroundImage';
import Loader from 'ui/atoms/Loader/Loader';
import Typography from 'ui/atoms/Typography/Typography';
import { BORDER_RADIUS_MULTIPLIER } from 'ui/shared/const';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import QRCodeValueUtil from 'utils/QRCodeValue/QRCodeValue.util';

type LandingType = 'access' | 'config';
export interface Props {
  type: LandingType;
}

const Container = styled.div`
  ${({ theme: { isMobileLandscape } }) => (isMobileLandscape ? 'overflow: auto; height: 100vh;' : '')};
`;

const Wrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  gap: 2rem;

  ${({ theme: { isMobile } }) => (isMobile ? `margin: 2rem 0;` : ``)};
`;

const Frame = styled.div`
  background-color: ${(props) => props.theme.colors.bgLight};
  display: flex;
  flex-direction: col;
  gap: 4.375rem;
  border-radius: ${(props) => props.theme.borderRadius * BORDER_RADIUS_MULTIPLIER}px;

  ${({ theme: { isMobile } }) =>
    isMobile
      ? `flex-direction: column;
  padding: 1.5rem;
  gap: 1.5rem;
  margin-top: 2rem;`
      : `flex-direction: row;
  padding: 3rem 5rem;
  gap: 4.375rem;`};
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${({ theme: { isMobile } }) => (isMobile ? `width: calc(90vw - 3rem);` : 'width: 26.375rem;')};
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;

  ${({ theme: { isMobile } }) => (isMobile ? `flex-direction: row;` : 'flex-direction: column;')};
`;

const GooglePlayButton = styled.div`
  background-image: url(${GooglePlay});
  width: 7.5rem;
  height: 2.1875rem;
`;

const AppStoreButton = styled.div`
  background-image: url(${AppStore});
  width: 7.5rem;
  height: 2.5rem;
`;

const LandingPage: React.FC<React.PropsWithChildren<Props>> = ({ type }) => {
  const { translation } = useTranslation();
  const { isMobile } = useReactiveVar(uiVar);
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  return (
    <Container>
      <BackgroundImage id="background" />
      <Wrapper>
        {!isMobile && <Title />}
        <Frame>
          <ComponentWrapper flexDirection="column" gap="1.5rem">
            <Typography variant={isMobile ? 'title4' : 'title1'} color="dTextHigh">
              {`ATLAS ${type === 'access' ? `${translation.access}` : `${translation.config}`} ${
                translation.application
              }`}
            </Typography>
            {isMobile ? (
              <TextWrapper>
                {type === 'access' ? (
                  <Typography variant="body4" color="dTextHigh">
                    {translation.link_profile_to_atlas + type === 'access'
                      ? `${translation.access}`
                      : `${translation.config}`}{' '}
                    {translation.application}
                  </Typography>
                ) : (
                  <Typography variant="body3" color="dTextHigh">
                    {translation.installer_invite_download_config}
                  </Typography>
                )}
                <Typography variant="body4" color="dTextHigh">
                  {type === 'access' ? translation.download_access_app : translation.download_config_app}.
                </Typography>
              </TextWrapper>
            ) : (
              <TextWrapper>
                {type === 'access' ? (
                  <Typography variant="body3" color="dTextHigh">
                    {translation.link_profile_to_atlas_access_app}
                  </Typography>
                ) : (
                  <Typography variant="body3" color="dTextHigh">
                    {translation.installer_invite_scan_qr}
                  </Typography>
                )}
                <Typography variant="body3" color="dTextHigh">
                  {`1. ${type === 'access' ? translation.download_access_app : translation.download_config_app}.`}
                </Typography>
                <Typography variant="body3" color="dTextHigh">
                  {`2. ${translation.onboarding_open_qr_scanner}.`}
                </Typography>
                <Typography variant="body3" color="dTextHigh">
                  {`3. ${translation.register_device_scan_qr}`}
                </Typography>
              </TextWrapper>
            )}
          </ComponentWrapper>
          <ComponentWrapper flexDirection="column" gap="5rem">
            {code ? (
              !isMobile && (
                <ComponentWrapper
                  alignItems="center"
                  justifyContent="center"
                  width="14rem"
                  height="14rem"
                  id="registration-page-qr-code"
                >
                  <QRCode value={QRCodeValueUtil.getQrCodeValue(code)} />
                </ComponentWrapper>
              )
            ) : (
              <ComponentWrapper
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="14rem"
                height="14rem"
              >
                <Loader size={64} />
                <Typography variant="title4" color="dTextHigh">
                  {translation.generating_qr}
                </Typography>
              </ComponentWrapper>
            )}
            <ButtonsWrapper>
              {type === 'access' && (
                <a
                  href="https://play.google.com/store/apps/details?id=com.tkhsecurity.siqura\u0026hl=en_US/"
                  target="_blank"
                  rel="noreferrer"
                  id="google-play-button"
                >
                  <GooglePlayButton />
                </a>
              )}
              <a
                href={
                  type === 'access'
                    ? 'https://apps.apple.com/eng/app/atlas-access/id1625855308'
                    : 'https://apps.apple.com/nl/app/atlas-config/id1634787760?l'
                }
                target="_blank"
                rel="noreferrer"
                id="app-store-button"
              >
                <AppStoreButton />
              </a>
            </ButtonsWrapper>
          </ComponentWrapper>
        </Frame>
      </Wrapper>
    </Container>
  );
};

export default LandingPage;
